import React, { useState, useEffect } from 'react';
import { Code2, Terminal, Database, RefreshCw, ExternalLink, Wrench, AlertOctagon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useToast } from '../../../components/common/FeedbackSystem';

interface ToolCard {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  path: string;
  status?: 'success' | 'warning' | 'error';
  lastRun?: string;
}

const tools: ToolCard[] = [
  {
    id: 'test-api',
    title: 'Test APIs',
    description: 'Testez et validez les intégrations API externes',
    icon: Code2,
    path: '/mon-espace/test-api',
    status: 'success',
    lastRun: '2025-06-06 12:00'
  },
  {
    id: 'test-edge',
    title: 'Test Edge Functions',
    description: 'Validez les fonctions Edge et leur déploiement',
    icon: Terminal,
    path: '/mon-espace/test-edge',
    status: 'success',
    lastRun: '2025-06-06 11:30'
  },
  {
    id: 'test-keywords',
    title: 'Test Keywords',
    description: 'Testez l\'analyse et le classement des mots-clés',
    icon: Database,
    path: '/mon-espace/test-keywords',
    status: 'warning',
    lastRun: '2025-06-06 10:45'
  }
];

export function DeveloperToolsSection() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [secretClickCount, setSecretClickCount] = useState(0);
  const [showSecretButton, setShowSecretButton] = useState(false);
  const [isErasing, setIsErasing] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  
  useEffect(() => {
    // Réinitialiser le compteur après un certain délai sans interaction
    const resetTimer = setTimeout(() => {
      if (secretClickCount > 0) {
        setSecretClickCount(0);
      }
    }, 3000);
    
    return () => clearTimeout(resetTimer);
  }, [secretClickCount]);
  
  // Détecteur de combinaison de touches secrète
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Combinaison secrète : Shift + Alt + X
      if (e.shiftKey && e.altKey && e.code === 'KeyX') {
        setSecretClickCount((prev) => {
          const newCount = prev + 1;
          if (newCount >= 3) {
            setShowSecretButton(true);
            return 0;
          }
          return newCount;
        });
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const getStatusColor = (status: string | undefined) => {
    switch (status) {
      case 'success':
        return 'text-green-600 bg-green-50';
      case 'warning':
        return 'text-yellow-600 bg-yellow-50';
      case 'error':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };
  
  // Fonction pour effacer toutes les données sauf les clés API
  const eraseAllData = async () => {
    try {
      setIsErasing(true);
      
      // Liste des tables à vider (EXCLUT tables sensibles ou spécifiques)
      const tablesToClear = [
        'prospects',
        'reponses_quiz',
        'keyword_analysis_results',
        'pagespeed_results',
        'instant_page_analysis',
        'google_reviews_results',
        'personalized_intros',
        'email_campaigns',
        'emelia_events',
        'cms_detection_results'
      ];
      
      // Tables à préserver explicitement
      const preservedTables = ['api_keys', 'quiz_questions', 'settings', 'messages'];
      
      // Utiliser directement une procédure SQL pour vider les tables avec TRUNCATE
      // C'est plus efficace et évite les problèmes avec les clés étrangères
      let successCount = 0;
      let errorCount = 0;
      
      for (const table of tablesToClear) {
        try {
          
          // 1. D'abord essayons de compter les lignes pour confirmer que la table existe
          const { count, error: countError } = await supabase
            .from(table)
            .select('*', { count: 'exact', head: true });
          
          if (countError) {
            console.error(`Table ${table} inaccessible:`, countError);
            errorCount++;
            continue;
          }
          
          
          // 2. Si aucune ligne à supprimer, passer à la table suivante
          if (count === 0) {
            successCount++;
            continue;
          }
          
          // 3. Exécuter un appel SQL direct pour contourner les limitations de l'API
          const { error } = await supabase.rpc('truncate_table', { table_name: table });
          
          if (error) {
            console.error(`Erreur lors du vidage de la table ${table} via RPC:`, error);
            
            // Plan B: essayer la suppression standard si la RPC échoue
            const { error: deleteError } = await supabase
              .from(table)
              .delete()
              .neq('id', '00000000-0000-0000-0000-000000000000');
            
            if (deleteError) {
              console.error(`Échec de la suppression pour ${table}:`, deleteError);
              errorCount++;
            } else {
              successCount++;
            }
          } else {
            successCount++;
          }
        } catch (e) {
          console.error(`Exception pour la table ${table}:`, e);
          errorCount++;
        }
      }
      
      // Vérification après suppression
      let totalRemaining = 0;
      
      for (const table of tablesToClear) {
        try {
          const { count, error } = await supabase
            .from(table)
            .select('*', { count: 'exact', head: true });
            
          if (!error) {
            totalRemaining += count || 0;
          }
        } catch (e) {
          console.error(`Erreur de vérification pour ${table}:`, e);
        }
      }
      
      // Rapport de suppression
      let summary = `${successCount} tables nettoyées avec succès. Tables préservées: api_keys, quiz_questions, settings, messages. `;
      if (errorCount > 0) {
        summary += `Échec pour ${errorCount} tables. `;
      }
      summary += `${totalRemaining} enregistrements restants au total.`;
      
      showSuccess(
        'Base de données nettoyée',
        summary
      );
      
      // Cacher le bouton après utilisation
      setShowSecretButton(false);
    } catch (error) {
      console.error('Erreur lors du nettoyage de la base de données:', error);
      showError(
        'Erreur',
        `Une erreur est survenue lors du nettoyage: ${error instanceof Error ? error.message : String(error)}`
      );
    } finally {
      setIsErasing(false);
      setConfirmDialog(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Wrench className="h-6 w-6 text-gray-900" />
          <h2 className="text-xl font-semibold text-gray-900">Outils de développement</h2>
        </div>
        <button
          onClick={() => {
            // Refresh all tools status
          }}
          className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualiser
        </button>
      </div>

      {/* Tools grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {tools.map((tool) => {
          const Icon = tool.icon;
          const statusColor = getStatusColor(tool.status);

          return (
            <div
              key={tool.id}
              className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-blue-500 rounded-lg shadow hover:shadow-md transition-shadow duration-200"
            >
              <div>
                <span className={`inline-flex p-3 rounded-lg ${statusColor}`}>
                  <Icon className="h-6 w-6" aria-hidden="true" />
                </span>
              </div>
              <div className="mt-4">
                <h3 className="text-lg font-medium">
                  <button
                    onClick={() => navigate(tool.path)}
                    className="focus:outline-none"
                  >
                    <span className="absolute inset-0" aria-hidden="true" />
                    {tool.title}
                  </button>
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  {tool.description}
                </p>
              </div>
              <div className="mt-4 flex items-center justify-between text-sm">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full font-medium ${statusColor}`}>
                  {tool.status}
                </span>
                {tool.lastRun && (
                  <span className="text-gray-500">
                    Dernière exécution: {tool.lastRun}
                  </span>
                )}
              </div>
              <div className="mt-4 flex items-center text-sm text-blue-600 group-hover:text-blue-700">
                <ExternalLink className="h-4 w-4 mr-2" />
                Ouvrir l'outil
              </div>
            </div>
          );
        })}
      </div>

      {/* Bouton caché */}
      {showSecretButton && (
        <div className="mt-8 bg-red-50 border border-red-300 rounded-lg shadow-sm p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <AlertOctagon className="h-6 w-6 text-red-500" aria-hidden="true" />
            </div>
            <div className="ml-3 w-full">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-red-800">
                  Outil de maintenance avancée
                </h3>
                <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">DANGER</span>
              </div>
              <div className="mt-2 text-sm text-red-700">
                <p>
                  Cette action va supprimer définitivement tous les prospects et résultats de quizz de la base de données.
                  Cette action est irréversible.
                </p>
              </div>
              <div className="mt-4">
                {!confirmDialog ? (
                  <button
                    type="button"
                    onClick={() => setConfirmDialog(true)}
                    className="inline-flex items-center px-4 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Effacer toutes les données
                  </button>
                ) : (
                  <div className="space-y-3 bg-white p-4 rounded-md border border-red-200">
                    <p className="text-sm font-medium text-red-800">
                      Êtes-vous absolument certain de vouloir supprimer toutes les données ?
                    </p>
                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={eraseAllData}
                        disabled={isErasing}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        {isErasing ? 'Suppression en cours...' : 'Confirmer la suppression'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDialog(false)}
                        disabled={isErasing}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Documentation link */}
      <div className="mt-8 bg-gray-50 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <Terminal className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-gray-900">
              Documentation développeur
            </h3>
            <div className="mt-2 text-sm text-gray-500">
              <p>
                Accédez à la documentation complète des outils de développement et des APIs.
              </p>
            </div>
            <div className="mt-3">
              <a
                href="#"
                className="text-sm font-medium text-blue-600 hover:text-blue-500"
              >
                Voir la documentation <span aria-hidden="true">&rarr;</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DeveloperToolsSection;
